<script lang="ts">
	//Services
	import compareVersions from 'compare-versions';
	import { format } from 'svelte-i18n';
	import { version } from '../../package.json';
	// Svelte hooks
	import { beforeUpdate, onDestroy, onMount, tick } from 'svelte';
	// Stores
	import { user_data, profile_data, user_settings, home_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { app, os, ios_version, android_version } from 'src/stores/session/native-bridge';
	import { is_tablet, is_mobile, routeParams } from 'src/stores/ui/viewport';
	import { showBleControlSetupModal } from 'src/stores/features/ble/control-device';
	import { dashboards, dashboardTabs } from 'src/stores/features/dashboards';
	import { resetDeviceMainFilter, resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import { authLockedDeviceModal } from 'src/stores/device-runtime/firmware-ui';
	import { rebootRequired } from 'src/stores/device-runtime/states';
	import { showNewsletterModal } from 'src/stores/ui/on-log-modals';
	import { showCalibrationHasStartedModal } from 'src/stores/ui/calibration-modals';
	import { globalLoaderMsg, showGlobalLoader } from 'src/stores/ui/global-loader';
	import { sidepanelDesktopOnTop, sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { homes } from 'src/stores/account/home';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { edit } from 'src/stores/ui/edit_lists';
	import {
		editChromeExitDissolving,
		compactEditChrome,
		requestExitEditMode,
	} from 'src/helpers/edit_chrome_helpers';

	// Helpers
	import { trimLastChar } from '../helpers/utilities';
	import {
		getMainLayoutSettingsTabs,
		menuItemsNewLayout,
		menuItemsNewLayoutMore,
		tabItemsConsumption,
		tabItemsEventLog,
		tabItemsExploreShelly,
		tabItemsMyHome,
		filterMenuItemsForSub,
		getLandingHref,
		getNewLayoutLandingItemHref,
		findActiveMenuItemIndex,
		isFlatMenuHref,
	} from '../helpers/main_menu';
	import type { MenuItemDef as MenuItemType } from '../helpers/main_menu';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { mapIconWeightToSolid } from 'src/helpers/new-layout-helpers';
	import {
		resetSavedScrollPosition,
		savePrevScrollPosition,
		setPrevScrollPosition,
	} from '../helpers/scroll_position_helpers';
	import { attachTabSwipeNavigate, nextTabIndexForSwipe } from '../helpers/tab_swipe_navigation';
	import { playTabSwipeContentAnimation } from '../helpers/tab_swipe_animation';
	import {
		getIsActivityLogVisible,
		getIsCameraMonitoringVisible,
		getIsEnergyMonitoringVisible,
		isDashTabVisible,
	} from '../components/Lists/list-helpers';
	import {
		consumeTabSwipeAnimPending,
		isTabSwipeIosEdgeClearanceEnabled,
		isTabSwipeNavigateEnabled,
		requestTabSwipeContentAnimation,
		tabSwipeAnimDirection,
		tabSwipeAnimationEnabled,
		tabSwipeIosEdgeClearanceEnabled,
	} from 'src/stores/features/dev/tab-swipe-navigate';
	import { backForwardNavigation } from 'src/services/native_container_requests_ios';
	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { isDevModalAvailable } from 'src/helpers/dev_tools';
	import { getRoomTabKeys, pushRoomListType } from 'src/helpers/service_rooms_helpers';
	import { allEnergyWidgetsGalleryEnabled } from 'src/stores/features/dev/all-energy-widgets-gallery';
	import { allDeviceWidgetsGalleryEnabled } from 'src/stores/features/dev/all-device-widgets-gallery';
	// Types
	import type { TypeTabs } from '../types/Tabs';
	// Routes
	import Router, { push, location, replace, params } from 'svelte-spa-router';
	import HomeBackground from './HomeBackground.svelte';
	// Design system components
	import {
		MainLayout,
		Header,
		IconButton,
		ButtonV2,
		Tabs,
		Tab,
		Text,
		LoaderDialog,
		RectangularAvatar,
	} from '@Shelly/shelly-design-system';
	import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
	import { getBackgroundColorImage } from 'src/helpers/background_color_helpers';
	import { load_image_from } from 'src/helpers/interface_helpers';
	// Components
	import AlexaIntegrationPrompt from 'src/components/AlexaIntegrationPrompt.svelte';
	import DashListsRender from './DashListsRender.svelte';
	import HomeListsRender from './HomeListsRender.svelte';
	import ConsumptionCurrent from '../components/Consumption/ConsumptionCurrent.svelte';
	import ConsumptionOverall from '../components/Consumption/ConsumptionOverall.svelte';
	import UserSettings from './UserSettings.svelte';
	import GeneralSettings from './GeneralSettings.svelte';
	import HomeSettings from './HomeSettings.svelte';
	import NotFound from './NotFound.svelte';
	import AmazonAlexaAppLinking from './AmazonAlexaAppLinking.svelte';
	import ZendureIntegration from './ZendureIntegration.svelte';
	import LoqedIntegration from './LoqedIntegration.svelte';
	import Integrations from './Integrations.svelte';
	// import AppToAppAlexa from './AppToAppAlexa.svelte';
	import PremiumSettings from './PremiumSettings.svelte';
	import DashboardEditTabsModal from 'src/components/Lists/Dashboards/DashboardEditTabsModal.svelte';
	import EventLog from '../components/EventLog/EventLog.svelte';
	import CameraMonitoringRoute from 'src/components/Camera/CameraMonitoringRoute.svelte';
	import CameraDashActions from 'src/components/Camera/MonitoringDashboard/CameraDashActions.svelte';
	import EnergyMonitoringRoute from 'src/components/EnergyMonitoring/EnergyMonitoringRoute.svelte';
	import EnergyDashActions from 'src/components/EnergyMonitoring/EnergyDashActions.svelte';
	import EmEnergyDashboardSettingsModal from 'src/components/EnergyMonitoring/EmEnergyDashboardSettingsModal.svelte';
	import AllEnergyWidgetsActions from 'src/components/EnergyMonitoring/AllEnergyWidgetsActions.svelte';
	import { isAllEnergyWidgetsDashboardId } from 'src/components/EnergyMonitoring/v2/all-energy-widgets';
	import AllDeviceWidgetsActions from 'src/components/Dashboards/AllDeviceWidgetsActions.svelte';
	import { isAllDeviceWidgetsDashboardId } from 'src/components/Dashboards/all-device-widgets';
	import {
		isEnergyMonitoringLocation,
		resolveEnergyMonitoringDashTabIndex,
	} from 'src/components/EnergyMonitoring/helpers/constants';
	import IncludeAddModals from '../components/IncludeAddModals.svelte';
	import UnlockAuthLockedDevice from '../components/Devices/DeviceWizard/UnlockAuthLockedDevice.svelte';
	import DiscoveredDevicesListHolder from '../components/Lists/Devices/DiscoveredDevicesListHolder.svelte';
	import NewsletterModal from './NewsletterModal.svelte';
	import NewLayoutOnboardingModal from 'src/components/Settings/AccountSettings/ExperimentalFeatures/NewLayoutOnboardingModal.svelte';
	import BleControlModal from 'src/components/BleControlModal.svelte';
	// import GeofenceRangeModal from './GeofenceRangeModal.svelte';
	import RebootPrompt from 'src/components/RebootDialog/RebootPrompt.svelte';
	import DropdownProfile from 'src/components/Profiles/DropdownProfile.svelte';
	import SimpleLogout from 'src/components/Profiles/SimpleLogout.svelte';
	import EnergyDistribution from 'src/components/Consumption/EnergyDistribution/EnergyDistribution.svelte';
	import TabRightSlotContent from 'src/components/TabRightSlotContent.svelte';
	import Discounts from './Discounts.svelte';
	import CalibrationHasStartedModal from './CalibrationHasStartedModal.svelte';
	import AddAction from 'src/components/AddActions/AddAction.svelte';
	import ModalsContainer from 'src/components/Modals/ModalsContainer.svelte';
	import SidePanelProvider from 'src/components/Sidepanel/SidePanelProvider.svelte';
	import {
		bridgeTouchCancelToTouchEnd,
		setSidePanelState,
	} from 'src/components/Sidepanel/sidepanel-helpers';
	import NotificationCenter from 'src/components/Notifications/NotificationCenter.svelte';
	import MainMenuProvider from 'src/components/MainMenu/MainMenuProvider.svelte';
	import FeedbackAndHelp from './FeedbackAndHelp.svelte';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import NewDashManagementModal from 'src/components/Lists/Dashboards/Components/NewDashManagementModal.svelte';
	import AddEditDashboardNewLayout from 'src/components/Lists/Dashboards/AddEditDashboardNewLayout.svelte';
	import { isEmailUnverified } from 'src/helpers/email_verification_helpers';
	import { getDashRouteHref, DEFAULT_DASH_ID } from 'src/components/Dashboards/dash-helpers';
	import {
		getDashboardKind,
		isEnergyDashboard,
		isProtectedDashboard,
		usesEnergyWidgetCatalog,
	} from 'src/components/Dashboards/dashboard-kind';
	import DashActions from 'src/components/Dashboards/DashActions/DashActions.svelte';
	import DashWidgetActionModal from 'src/components/AddActions/Wizards/DashWidgetActionModal.svelte';
	import ListHeaderActions from 'src/components/Lists/ListHeaderActions.svelte';
	import RoomHeaderChrome from 'src/components/Lists/Rooms/RoomHeaderChrome.svelte';
	import { listHeader } from 'src/stores/list_header';

	import AIChatModal from 'src/components/AISceneChatbot/AIChatModal.svelte';
	import AIChatOnboardingModal from 'src/components/AISceneChatbot/AIChatOnboardingModal.svelte';
	import AIChatPrivacyPolicyModal from 'src/components/AISceneChatbot/AIChatPrivacyPolicyModal.svelte';
	import AiBreakdown from 'src/components/Consumption/AiBreakdown/components/AiBreakdown.svelte';
	import CuryIntegration from './CuryIntegration.svelte';
	import { showNewLayout, showNewLayoutOnboarding } from 'src/stores/toDelete/new_layout';
	import {
		createScrollChromeTracker,
		isScrollChromeTarget,
		resolveScrollChromeCollapsed,
		SCROLL_CHROME_HEADER_EXPAND_DISTANCE,
	} from 'src/helpers/scroll_chrome_helpers';
	import {
		headerScrollCollapseEnabled,
		isHeaderScrollCollapseEnabled,
	} from 'src/stores/features/dev/header-scroll-collapse';
	import {
		getHeaderChromePackageConfig,
		headerChromePackage,
	} from 'src/stores/features/dev/header-chrome';
	// Side-effect: applies `--header-content-fade-height` from the fade store.

	// Routes
	const mainRoutes = {
		'/': DashListsRender,
		'/event_log': EventLog,
		// '/dash/event_log': EventLog, // keep up in the list to override the wild card params
		'/dash/camera_monitoring': CameraMonitoringRoute, // Camera monitoring dashboard
		'/dash/energy_monitoring': EnergyMonitoringRoute,
		'/dash': DashListsRender,
		'/dash/': DashListsRender,
		'/dash/:id': DashListsRender,
		'/dash/:type/:id/': DashListsRender,

		'/home/discovered': DiscoveredDevicesListHolder,
		'/home/:type/:id/': HomeListsRender,
		'/home/:type/:id/:room_list': HomeListsRender,
		// '/:type/:id': HomeListsRender,

		'/cons/-1': ConsumptionCurrent,
		'/cons/0': ConsumptionOverall,
		'/cons/3': EnergyDistribution,
		'/cons/6': AiBreakdown,

		'/explore-shelly/': FeedbackAndHelp,
		'/explore-shelly/feedback': FeedbackAndHelp,
		'/explore-shelly/discounts': Discounts,

		'/settings/user': UserSettings,
		'/settings/user/*': UserSettings,
		'/settings/app': GeneralSettings,
		'/settings/home': HomeSettings,
		'/settings/home/*': HomeSettings,
		'/settings/app-to-app-alexa': AmazonAlexaAppLinking,
		'/settings/integrations/app-to-app-cury': CuryIntegration,
		'/settings/integrations/zendure': ZendureIntegration,
		'/settings/integrations/loqed': LoqedIntegration,
		'/settings/integrations': Integrations,
		// '/app-to-app-alexa': AmazonAlexaAppLinking,
		// '/app-to-app-alexa': AppToAppAlexa,
		// '/settings/changelog': History,
		'/settings/discounts': Discounts,
		'/settings/premium': PremiumSettings,
		// '/settings/discounts': Discounts,
		'*': NotFound,
	};

	const isApp = $app;

	const isDev = import.meta.env.DEV;
	let windowClientWidth: number;
	let showAddDashModal = false;
	let showDeleteDashModal = false;
	let showAddWidgetModal = false;
	let showNewDashModal = false;
	let showEnergyDashSettingsModal = false;

	let accountSwitchingEnabled = false;

	let dashboardsEdit = false;

	// let shouldScroll = false;

	let activeTabIndex = 0;

	let currentTabItems: TypeTabs = [];

	let menuTabsById: Record<string, TypeTabs> = {};

	let mainLayoutScrollRef: HTMLDivElement;

	let routeScrollRef: HTMLDivElement;

	let isHeaderCollapsed = false;
	const headerScrollChrome = createScrollChromeTracker();

	const handleHeaderCollapseOnScroll = (scrollTop: number, scrollEl?: HTMLElement) => {
		// Stay collapsed at the bottom (ignore overscroll bounce). Restore only after
		// a sustained upward scroll — not on tiny deltas.
		isHeaderCollapsed = resolveScrollChromeCollapsed(headerScrollChrome, scrollTop, {
			enabled: isHeaderScrollCollapseEnabled(),
			isMobile: !!$is_mobile,
			forceExpanded: !!$sidePanelStateCombined?.open,
			expandDistance: SCROLL_CHROME_HEADER_EXPAND_DISTANCE,
			lockExpandNearBottom: true,
			scrollEl: scrollEl ?? null,
		});
	};

	const handleHeaderDocumentScroll = (event: Event) => {
		if (!isScrollChromeTarget(event.target)) return;
		handleHeaderCollapseOnScroll(event.target.scrollTop, event.target);
	};

	const handleHeaderWindowScroll = () => {
		const scrollTop =
			window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
		const scrollEl =
			document.scrollingElement instanceof HTMLElement
				? document.scrollingElement
				: document.documentElement;
		handleHeaderCollapseOnScroll(scrollTop, scrollEl);
	};

	let menuItems = filterMenuItemsForSub(
		[...menuItemsNewLayout, ...menuItemsNewLayoutMore],
		isSubAccount(),
	);

	let activeMenuItemIndex = findActiveMenuItemIndex(menuItems, $location);

	let activeMenuItem: MenuItemType = menuItems[activeMenuItemIndex !== -1 ? activeMenuItemIndex : 0];
	let visualActiveMenuId: string | null = activeMenuItem?.id || null;
	let lastSyncedLocation = '';

	const resolveVisualMenuId = (path: string) => {
		if (menuItemsNewLayoutMore.some((item) => item.href && path.startsWith(item.href))) {
			return 'more-menu-item';
		}

		const matchedMenuItem = menuItems.find(
			(item) => typeof item.href === 'string' && path.startsWith(item.href),
		);
		return matchedMenuItem?.id || activeMenuItem?.id || null;
	};
	let useNewLayout = $showNewLayout; //check if its new layout and set the flag to true

	$: headerChromeConfig = getHeaderChromePackageConfig($headerChromePackage);
	$: headerChromeStyle = headerChromeConfig.chromeStyle;
	$: headerTabsStyle = headerChromeConfig.tabsStyle;
	$: isInRoomView = $location.startsWith('/home/room/');

	user_settings.subscribe((val) => {
		menuItems = filterMenuItemsForSub(
			[...menuItemsNewLayout, ...menuItemsNewLayoutMore],
			typeof val?.sub === 'number',
		);
	});

	if (isApp) {
		const isIOS = $os === 'IOS';
		const version = isIOS ? $ios_version : $android_version;
		const minVersion = isIOS ? '1.1.1' : '1.16.1';
		try {
			if (compareVersions.compare(version, minVersion, '>=')) {
				accountSwitchingEnabled = true;
			}
		} catch (error) {
			console.error('we could not compare versions');
		}
	} else {
		accountSwitchingEnabled = true;
	}

	if ($app) {
		tabItemsMyHome.update((arr) => {
			if (arr.find((tab) => tab.key === 'discovered')) return arr;
			return [
				...arr,
				{
					id: arr.length,
					key: 'discovered',
					text: 'tab_discovered',
					defaultText: 'discovered',
					route: '/discovered',
				},
			];
		});
	}

	//#regionStart Router
	/** Landing / legacy redirects. Do not call from `routes=` — keep Router map stable. */
	const handleMenuRoutes = (route = '') => {
		if (route.includes('integrations/app-to-app-alexa')) {
			replace('/settings/app-to-app-alexa');
		}

		if ($location === '/' || route === '/') {
			const landingHref = getNewLayoutLandingItemHref($profile_data?.menuOrder);

			if (landingHref === '/include_devices' || landingHref === '/dash') {
				let routeTo = '-1';
				if ($dashboardTabs.length > 2) {
					routeTo = $dashboardTabs.filter((tab) => tab.position == 0)[0]?.id || routeTo;
				}

				replace(`/dash/${routeTo}`);
			} else if (landingHref === '/home') {
				replace(`/home/rooms/-1`);
			} else {
				const currentLocation = Object.keys(mainRoutes).find((key) => key.includes(landingHref));
				mainRoutes['/'] = mainRoutes[currentLocation];
				push(currentLocation);
			}
		}
	};

	const routeLoading = (e) => {
		if (e.detail.location.includes('device') && !e.detail.location.includes('home')) {
			const device = $devices[e.detail.location.split('/')[2]];

			if (device.room_id < 0) {
				replace(`/home/devices/-1`);
			} else {
				replace(`/home/room/${device.room_id}/devices`);
			}

			setSidePanelState({
				type: 'device',
				id: device.id,
				open: true,
			});
		}
		handleMenuRoutes(e.detail.location);
	};

	const routeLoaded = () => {
		// it is good if we abstract this new logic so we can use it not only with temperatureUnit
		// ping Kaloyan when you are reviewing the changes
		// if ($location === '/settings/user' && $querystring === 'temperatureUnit') {
		// 	shouldScroll = true;
		// } else if ($location !== '/settings/user' && shouldScroll) {
		// 	shouldScroll = false;
		// }

		// if (shouldScroll) return;

		if (!$location.includes('rooms') && !$location.includes('room') && !$location.includes('dash'))
			return;

		setPrevScrollPosition(
			mainLayoutScrollRef?.querySelector('.scroll-content-new-layout') as HTMLDivElement,
		);
	};
	//#regionEnd Router

	//#regionStart Modals
	const handleKeydown = (e: KeyboardEvent) => {
		if (e.key !== 'Escape') return;

		// Open modals own Escape (Modal listens on window). Don't steal the event.
		const openModalsLength = document.querySelectorAll('.modal-container').length;
		if (openModalsLength > 0) return;

		// The expanded nav dock (assistant) closes itself on Escape.
		if (document.querySelector('.main-menu-floating.is-expanded')) return;

		if ($sidePanelStateCombined.open) {
			setSidePanelState({ open: false });
			return;
		}

		if ($edit) {
			e.preventDefault();
			void requestExitEditMode();
		}
	};

	//#regionEnd Modals

	//#regionStart Navigation and Tabs
	const handlerClickMenuItem = (menuItem: MenuItemType) => {
		const curentMenuItem = menuItem;
		const href = curentMenuItem.href;
		activeMenuItem = curentMenuItem;
		visualActiveMenuId = curentMenuItem.id;
		// captureSideNavMenuItemClick({
		// 	menuItemId: curentMenuItem.id,
		// 	menuItemHref: href,
		// 	menuItemLabel: curentMenuItem.labelDefault || curentMenuItem.label,
		// 	layout: 'new',
		// });

		setSidePanelState({
			type: href === '/home' ? 'rooms' : 'dashboard',
			id: '-1',
		});

		if (curentMenuItem.label === 'label_menu_items_assistant') {
			aiSceneChatStore.openModal();
			trackAiMenuItemFirstSeen();
			resetDeviceMainFilter();
			resetSavedScrollPosition(
				mainLayoutScrollRef?.querySelector('.scroll-content-new-layout') as HTMLDivElement,
			);
			return;
		}

		const tabIndex = 0;
		const menuItemTabs = menuTabsById[curentMenuItem.id] || [];
		const pageTab = menuItemTabs?.[tabIndex]?.route;

		if (href === '/dash') {
			//Load the dash on first position or go to dash list
			const dashHref = getDashRouteHref(Object.values($dashboards), href);
			push(dashHref);
		} else if (isFlatMenuHref(href)) {
			push(href);
		} else {
			push(href + (pageTab || ''));
		}

		resetDeviceMainFilter();
		resetSavedScrollPosition(
			mainLayoutScrollRef?.querySelector('.scroll-content-new-layout') as HTMLDivElement,
		);
	};

	const handlerClickTabItem = (index: number) => {
		activeTabIndex = index;

		setSidePanelState({
			type: currentTabItems[index].key,
			id: '-1',
		});

		const menuItemHref = activeMenuItem?.href ?? menuItems[0].href;
		push(menuItemHref + currentTabItems[index].route);
		resetDeviceMainFilter();
		resetSavedScrollPosition(
			mainLayoutScrollRef?.querySelector('.scroll-content-new-layout') as HTMLDivElement,
		);
	};

	// Selecting a dashboard tab opens it and marks it active via the current route.
	const handlerClickDashboardTab = (item: TypeTabs[number]) => {
		setSidePanelState({ type: 'dashboard', id: '-1' });
		push('/dash' + item.route);
		resetDeviceMainFilter();
		resetSavedScrollPosition(
			mainLayoutScrollRef?.querySelector('.scroll-content-new-layout') as HTMLDivElement,
		);
	};

	const getVisibleDashboardTabs = () =>
		$dashboardTabs.filter((item) =>
			isDashTabVisible(
				item,
				isActivityLogVisible,
				isCameraMonitoringVisible,
				isEnergyMonitoringVisible,
			),
		);

	/** Swipe left → next tab, swipe right → previous. Dash + Home only. No wrap. */
	const navigateTabBySwipe = (direction: 1 | -1) => {
		if ($location.startsWith('/dash')) {
			const tabs = getVisibleDashboardTabs();
			if (tabs.length < 2) return;
			const currentIndex = tabs.findIndex(
				(tab) => $dashboardTabs.indexOf(tab) === activeDashTabIndex,
			);
			const from = currentIndex >= 0 ? currentIndex : 0;
			const next = nextTabIndexForSwipe(from, direction, tabs.length);
			if (next === null) return;
			requestTabSwipeContentAnimation(direction);
			handlerClickDashboardTab(tabs[next]);
			return;
		}

		if ($location.startsWith('/home')) {
			if (isInRoomView) {
				const roomId = $location.split('/')[3];
				const roomTabs = getRoomTabKeys(roomId);
				if (roomTabs.length < 2) return;
				const currentType = $location.split('/')[4];
				const from = Math.max(
					0,
					roomTabs.findIndex((tab) => tab === currentType),
				);
				const next = nextTabIndexForSwipe(from, direction, roomTabs.length);
				if (next === null) return;
				requestTabSwipeContentAnimation(direction);
				pushRoomListType(roomId, roomTabs[next]);
				return;
			}

			if (currentTabItems.length < 2) return;
			const from = activeTabIndex >= 0 ? activeTabIndex : 0;
			const next = nextTabIndexForSwipe(from, direction, currentTabItems.length);
			if (next === null) return;
			requestTabSwipeContentAnimation(direction);
			handlerClickTabItem(next);
		}
	};

	/** iOS-only: disable WKWebView edge BF gestures on Dash/Home when clearance is on. */
	const syncIosEdgeClearance = (clearanceEnabled: boolean, path: string, sidepanelOpen: boolean) => {
		if (!isApp || $os !== 'IOS') return;
		if (sidepanelOpen) {
			backForwardNavigation(false);
			return;
		}
		const onSwipeTabs = path.startsWith('/dash') || path.startsWith('/home');
		backForwardNavigation(!(clearanceEnabled && onSwipeTabs));
	};
	//#regionEnd Navigation and Tabs

	function trackAiMenuItemFirstSeen() {
		if ($profile_data?.aiMenuItemSeen) return;
		const dateNow = new Date();
		save_profile('aiMenuItemSeen', { seen: true, date: dateNow });
		profile_data.set({ ...$profile_data, aiMenuItemSeen: { seen: true, date: dateNow } });
	}

	function markNewLayoutOnboardingSeen() {
		save_profile('newLayoutOnboardingSeen', { seen: true });
		profile_data.set({ ...$profile_data, newLayoutOnboardingSeen: { seen: true } });
	}

	$: isActivityLogVisible = getIsActivityLogVisible($home_data);
	$: isCameraMonitoringVisible = getIsCameraMonitoringVisible($home_data);
	$: isEnergyMonitoringVisible = getIsEnergyMonitoringVisible($home_data);

	// Quick filters published by a list holder. Only flat routes lend the rail
	// out, so route-driven tabs and quick filters never compete. The flat check
	// reads `$location` rather than `currentTabItems`: `beforeUpdate` rewrites
	// that array on every cycle, so reading it from an effect never settles.
	$: headerQuickTabs = isFlatMenuHref($location) ? ($listHeader?.tabs ?? null) : null;
	$: headerQuickTabIndex = headerQuickTabs
		? Math.max(
				0,
				headerQuickTabs.tabs.findIndex((tab) => tab.id === headerQuickTabs.activeId),
			)
		: 0;
	$: if ($location !== lastSyncedLocation) {
		lastSyncedLocation = $location;
		visualActiveMenuId = resolveVisualMenuId($location);
	}

	$: ($location,
		savePrevScrollPosition(
			mainLayoutScrollRef?.querySelector('.scroll-content-new-layout') as HTMLDivElement,
		));
	$: ($location, resetFiltersAllDevicesList());
	$: if ($location === '/') {
		handleMenuRoutes('/');
	}
	$: if ($location === '/dash' || $location === '/dash/') {
		replace(`/dash/${DEFAULT_DASH_ID}`);
	}
	$: currentDashId = $params?.id ?? $location.split('/').filter(Boolean)?.[1] ?? '';
	$: currentDashboard = $dashboards?.[currentDashId];
	$: currentDashboardKind = getDashboardKind(currentDashboard);
	$: isCameraMonitoring = $location === '/dash/camera_monitoring' || currentDashboardKind === 'camera';
	$: isAllEnergyWidgets =
		isDevModalAvailable($user_data?.email) &&
		$allEnergyWidgetsGalleryEnabled &&
		isAllEnergyWidgetsDashboardId(currentDashId);
	$: isAllDeviceWidgets =
		isDevModalAvailable($user_data?.email) &&
		$allDeviceWidgetsGalleryEnabled &&
		isAllDeviceWidgetsDashboardId(currentDashId);
	$: isEnergyMonitoring =
		isEnergyMonitoringLocation($location) ||
		isEnergyDashboard(currentDashboard) ||
		isAllEnergyWidgets;
	$: energyWidgetPickerOnly = isEnergyMonitoring || usesEnergyWidgetCatalog(currentDashboard);
	$: isDashWidgetList =
		$location.indexOf('dash') !== -1 &&
		$params !== null &&
		!isCameraMonitoring &&
		!isEnergyMonitoring &&
		!isAllDeviceWidgets;
	// Id of the currently opened dashboard. `$params` isn't reliable at this layout level
	// (it can be a non-null object without `id`), so fall back to parsing `$location`.
	// Index of the dashboard tab matching the currently opened dashboard (route is `/<id>`).
	$: activeDashTabIndex = resolveEnergyMonitoringDashTabIndex(
		$dashboardTabs,
		$location,
		currentDashId,
	);

	/** Home solid/gradient token used when a dashboard has no dedicated backgroundColor. */
	$: homeBackgroundToken = getBackgroundColorImage($profile_data?.layout?.backgroundColor)
		? ($profile_data.layout.backgroundColor as AvatarColorVariantTypes)
		: undefined;

	// The plate behind the tab icon must match the dashboard page background exactly.
	// The page (HomeBackground) renders the token as a pre-rendered image darkened by
	// the shared overlay, so resolve the SAME asset here and composite the SAME overlay
	// (via --home-background-overlay-opacity) on top — instead of the `bg-<token>` color
	// utility, whose gradient definition and full brightness never matched the page.
	$: resolveDashTabAvatar = (item: { icon?: string; backgroundColor?: string }) => {
		const icon = mapIconWeightToSolid(item.icon || 'fat fa-objects-column');
		const token = (item.backgroundColor?.trim() || homeBackgroundToken) as
			| AvatarColorVariantTypes
			| undefined;
		const tokenImage = getBackgroundColorImage(token);

		if (tokenImage) {
			const overlay = 'rgba(0, 0, 0, var(--home-background-overlay-opacity))';
			return {
				icon,
				backgroundImage: `linear-gradient(${overlay}, ${overlay}), url("${load_image_from(
					tokenImage.thumb,
				)}")`,
				backgroundColor: undefined as AvatarColorVariantTypes | undefined,
			};
		}

		return { icon, backgroundImage: undefined, backgroundColor: token };
	};

	// The default dashboard cannot be deleted.
	$: isDefaultDash = isProtectedDashboard($dashboards?.[currentDashId] ?? currentDashId);
	// Actions surfaced under the dashboard kebab (⋮), grouped per the FE-1199 decision:
	// Manage widgets / Manage dashboard / (standalone) Add dashboard.
	$: dashMenuListItemsData = [
		{
			title: $format('manage_widgets_group_title', { default: 'Manage widgets' }),
			isGroupTitle: true,
			items: [
				{
					title: $format('label_edit_widgets', { default: 'Edit widgets' }),
					icon: 'fas fa-pen-to-square',
					disabled: $dashboards?.[currentDashId]?.dashWidgets?.length === 0,
					onClick: () => edit.set(true),
				},
				{
					title: $format('label_add_widget', { default: 'Add widget' }),
					icon: 'fa-solid fa-rectangle-history-circle-plus',
					onClick: () => {
						showAddWidgetModal = true;
					},
				},
			],
		},
		{
			title: $format('manage_dashboard_group_title', { default: 'Manage dashboard' }),
			isGroupTitle: true,
			items: [
				{
					title: $format('label_dashboard_settings', { default: 'Dashboard Settings' }),
					icon: 'fa-solid fa-objects-column',
					onClick: () => {
						showAddDashModal = true;
					},
				},
				{
					title: $format('label_reorder_dashboards', { default: 'Reorder dashboards' }),
					icon: 'fa-solid fa-arrows-up-down-left-right',
					disabled: $dashboardTabs.length <= 1,
					onClick: () => {
						dashboardsEdit = true;
					},
				},
				{
					title: $format('label_delete_dashboard', { default: 'Delete dashboard' }),
					icon: `fas fa-trash${isDefaultDash ? '' : ' text-danger'}`,
					iconMobile: 'fa-light fa-trash',
					titleClasses: isDefaultDash ? '' : 'text-danger',
					disabled: isDefaultDash,
					onClick: () => {
						showDeleteDashModal = true;
					},
				},
			],
		},
		{
			title: '',
			items: [
				{
					title: $format('label_add_new_dashboard', { default: 'Add dashboard' }),
					icon: 'fa-solid fa-square-plus',
					onClick: () => {
						showNewDashModal = true;
					},
				},
			],
		},
	];

	// Camera monitoring kebab: same "Manage dashboard" groups as other dash tabs.
	// Settings/Delete stay disabled — camera monitoring is a system dashboard, not a
	// user-owned dash record.
	$: cameraManageDashboardMenuItems = [
		{
			title: $format('manage_dashboard_group_title', { default: 'Manage dashboard' }),
			isGroupTitle: true,
			items: [
				{
					title: $format('label_dashboard_settings', { default: 'Dashboard Settings' }),
					icon: 'fa-solid fa-objects-column',
					disabled: true,
					onClick: () => {},
				},
				{
					title: $format('label_reorder_dashboards', { default: 'Reorder dashboards' }),
					icon: 'fa-solid fa-arrows-up-down-left-right',
					disabled: $dashboardTabs.length <= 1,
					onClick: () => {
						dashboardsEdit = true;
					},
				},
				{
					title: $format('label_delete_dashboard', { default: 'Delete dashboard' }),
					icon: 'fas fa-trash',
					iconMobile: 'fa-light fa-trash',
					disabled: true,
					onClick: () => {},
				},
			],
		},
		{
			title: '',
			items: [
				{
					title: $format('label_add_new_dashboard', { default: 'Add dashboard' }),
					icon: 'fa-solid fa-square-plus',
					onClick: () => {
						showNewDashModal = true;
					},
				},
			],
		},
	];

	// Energy dashboard actions use the standard dashboard editor. Presets are immutable after creation.
	$: energyManageDashboardMenuItems = [
		{
			title: $format('manage_dashboard_group_title', { default: 'Manage dashboard' }),
			isGroupTitle: true,
			items: [
				{
					title: $format('label_dashboard_settings', { default: 'Dashboard Settings' }),
					icon: 'fa-solid fa-objects-column',
					onClick: () => {
						showEnergyDashSettingsModal = true;
					},
				},
				{
					title: $format('label_reorder_dashboards', { default: 'Reorder dashboards' }),
					icon: 'fa-solid fa-arrows-up-down-left-right',
					disabled: $dashboardTabs.length <= 1,
					onClick: () => {
						dashboardsEdit = true;
					},
				},
				{
					title: $format('label_delete_dashboard', { default: 'Delete dashboard' }),
					icon: 'fas fa-trash',
					iconMobile: 'fa-light fa-trash',
					disabled: true,
					onClick: () => {},
				},
			],
		},
		{
			title: '',
			items: [
				{
					title: $format('label_add_new_dashboard', { default: 'Add dashboard' }),
					icon: 'fa-solid fa-square-plus',
					onClick: () => {
						showNewDashModal = true;
					},
				},
			],
		},
	];

	beforeUpdate(() => {
		const _tabItemsConsumption = tabItemsConsumption
			.slice()
			.filter((tab) => tab.key !== 'monthly_reports' && tab.key !== 'power_prices');
		const settingsTabs = getMainLayoutSettingsTabs();

		menuTabsById = {
			dash: $dashboardTabs,
			home: $tabItemsMyHome,
			energy: _tabItemsConsumption,
			eventLog: tabItemsEventLog,
			assistant: settingsTabs,
			exploreShelly: tabItemsExploreShelly,
			settings: settingsTabs,
			subscription: [],
		};

		const testUser = isDevServerUser(['repo', 'nb']) || isT1User() || isDev;
		Object.keys(menuTabsById).forEach((id) => {
			menuTabsById[id] = menuTabsById[id].filter((i) => !i.testUser || testUser);
		});

		//get id of the current item of main menu
		// The More > "Subscription" entry points to '/settings/premium', which lives
		// inside the Settings section. Let the Settings menu item own every '/settings'
		// route so its header tabs stay visible (Subscriptions tab gets highlighted),
		// instead of the more-specific subscription href winning the match.
		const settingsMenuItemIndex = menuItems.findIndex((item) => item.href === '/settings');
		if (settingsMenuItemIndex > -1 && $location.startsWith('/settings')) {
			activeMenuItemIndex = settingsMenuItemIndex;
		} else {
			activeMenuItemIndex = findActiveMenuItemIndex(menuItems, $location);
		}

		activeMenuItem = menuItems[activeMenuItemIndex !== -1 ? activeMenuItemIndex : 0];

		// get tabs in current item of main menu
		const activeMenuItemId = activeMenuItem?.id;
		const fallbackId = menuItems[0]?.id;
		currentTabItems =
			(activeMenuItemId && menuTabsById[activeMenuItemId]) ||
			(fallbackId ? menuTabsById[fallbackId] : []) ||
			[];

		//get the active tab - get the default route from menuItemsTabs
		activeTabIndex = currentTabItems.findIndex((item) => {
			if (activeMenuItemIndex > -1) {
				// ToDo: might remove the second else condition at a later time, this does need refactoring.
				// Will refactor as soon as the main tabs routing logic is clear up
				// console.log('Menu item href ', menuItems[activeMenuItem].href);
				// console.log('Item route ', item.route);
				// console.log('Location ', $location);

				if (activeMenuItem.href === '/home') {
					return (
						activeMenuItem.href + item.route.substring(0, item.route.lastIndexOf('/')) ===
							$location.substring(0, $location.lastIndexOf('/')) ||
						trimLastChar(item.route.split('/')[1]) === $location.split('/')[2]
					);
				} else {
					//Bobi: Absolutely stupid fast fix
					//Alex: I will use it! ($location.match(''))
					if ($location.match('amazon')) {
						return (
							trimLastChar(item.route.split('/')[1]).split('?')[0] ===
							$location.split('/')[2]
						);
					} else if ($location.match('user')) {
						return item.route.split('/')[1].split('?')[0] === $location.split('/')[2];
						// Keep Integrations tab active when in integrations subroutes
					} else if ($location.match('integrations/')) {
						return item.route.split('/')[1] === $location.split('/')[2];
						// Keep Integrations tab active when in Alexa tab
						// It does not have 'integrations' in the route and we are selecting it manually
					} else if ($location.match('app-to-app-alexa')) {
						return item.route.split('/')[1] === 'integrations';
					} else {
						return (
							activeMenuItem.href + item.route === $location ||
							trimLastChar(item.route.split('/')[1]) === $location.split('/')[2]
						);
					}
				}
			} else {
				return 0;
			}
		});
	});

	let teardownTouchCancelBridge: (() => void) | undefined;
	let teardownTabSwipe: (() => void) | undefined;

	const syncTabSwipeNavigate = (host: HTMLElement | undefined) => {
		teardownTabSwipe?.();
		teardownTabSwipe = undefined;
		if (!host) return;

		teardownTabSwipe = attachTabSwipeNavigate(host, {
			isEnabled: () => {
				if (!isTabSwipeNavigateEnabled()) return false;
				if ($edit) return false;
				if ($sidePanelStateCombined?.open) return false;
				if (windowClientWidth > SystemScreenBreakpoints.TABLET_MAX) return false;
				return $location.startsWith('/dash') || $location.startsWith('/home');
			},
			edgeClearancePx: () => (isTabSwipeIosEdgeClearanceEnabled() && $os === 'IOS' ? 28 : 0),
			onSwipeLeft: () => navigateTabBySwipe(1),
			onSwipeRight: () => navigateTabBySwipe(-1),
		});
	};

	$: syncTabSwipeNavigate(mainLayoutScrollRef);

	$: {
		const clearanceEnabled = $tabSwipeIosEdgeClearanceEnabled;
		const path = $location ?? '';
		const sidepanelOpen = !!$sidePanelStateCombined?.open;
		if (sidepanelOpen) {
			syncIosEdgeClearance(clearanceEnabled, path, true);
		} else {
			// Defer so SidePanelProvider's enable-on-close does not win the race.
			tick().then(() => syncIosEdgeClearance(clearanceEnabled, path, false));
		}
	}

	// Play Option A enter animation only after a swipe-initiated Dash / Home tab change.
	let lastTabSwipeAnimLocation = '';
	$: if ($location && $location !== lastTabSwipeAnimLocation) {
		const previous = lastTabSwipeAnimLocation;
		lastTabSwipeAnimLocation = $location;
		const fromSwipe = consumeTabSwipeAnimPending();
		if (
			fromSwipe &&
			$tabSwipeAnimationEnabled &&
			mainLayoutScrollRef &&
			($location.startsWith('/dash') || $location.startsWith('/home')) &&
			(previous.startsWith('/dash') || previous.startsWith('/home'))
		) {
			requestAnimationFrame(() => {
				playTabSwipeContentAnimation(mainLayoutScrollRef, $tabSwipeAnimDirection);
			});
		}
	}

	// handle global modals escape key
	onMount(() => {
		document.addEventListener('keydown', handleKeydown);
		teardownTouchCancelBridge = bridgeTouchCancelToTouchEnd();
		document.addEventListener('scroll', handleHeaderDocumentScroll, {
			capture: true,
			passive: true,
		});
		window.addEventListener('scroll', handleHeaderWindowScroll, { passive: true });

		// The new layout onboarding now auto-opens once for users who haven't seen it.
		const seenViaRemovedLayoutToggle = $user_settings?.experimental_features?.nal !== undefined;
		const seenViaOnboardingFlag = !!$profile_data?.newLayoutOnboardingSeen?.seen;
		showNewLayoutOnboarding.set(!seenViaRemovedLayoutToggle && !seenViaOnboardingFlag);
	});

	onDestroy(() => {
		document.removeEventListener('keydown', handleKeydown);
		document.removeEventListener('scroll', handleHeaderDocumentScroll, true);
		window.removeEventListener('scroll', handleHeaderWindowScroll);
		teardownTouchCancelBridge?.();
		teardownTabSwipe?.();
	});

	// Tab / route remount: keep collapse sticky; only resync scroll baseline so
	// the next gesture is measured from the new page's scrollTop.
	$: if ($location) {
		const scrollHost =
			(mainLayoutScrollRef?.querySelector('.scroll-content-new-layout') as HTMLElement | null) ??
			mainLayoutScrollRef ??
			null;
		headerScrollChrome.downwardDistance = 0;
		headerScrollChrome.upwardDistance = 0;
		headerScrollChrome.lastScrollTop = scrollHost?.scrollTop ?? 0;
		isHeaderCollapsed = headerScrollChrome.isCollapsed;
	}

	// Dev flag off → always expanded.
	$: if (!$headerScrollCollapseEnabled) {
		if (isHeaderCollapsed) isHeaderCollapsed = false;
		if (headerScrollChrome.isCollapsed) headerScrollChrome.isCollapsed = false;
	}

	$: $is_mobile = windowClientWidth <= SystemScreenBreakpoints.MOBILE_MAX;
	$: $is_tablet = windowClientWidth <= SystemScreenBreakpoints.TABLET_MAX;
	$: isSettingsPage = $location.startsWith('/settings');
	$: activeHomeName = $homes.list.find((home) => home.isActive)?.name ?? '';
	$: isCompactEditChrome = $compactEditChrome;
</script>

<svelte:window bind:innerWidth={windowClientWidth} />
<!-- Version and tags in the app header (desktop/tablet only — avoids notch overlap on mobile) -->
{#if !$is_mobile}
	<div class="d-flex" style="position: absolute; top: 4px; right: 4px; z-index: 100; ">
		<Text styleType="miniature" class="text-primary mr-xs">
			{import.meta.env.MODE !== 'production' ? import.meta.env.MODE : ''}
		</Text>
		<Text styleType="miniature">
			{version}
		</Text>
	</div>
{/if}

<!-- Main Container App Layout -->
<HomeBackground let:backgroundImgSrc let:backgroundColor>
	<MainLayout
		{useNewLayout}
		floatingMainMenu
		headerCollapsed={isHeaderCollapsed || isCompactEditChrome}
		listEditing={isCompactEditChrome}
		{backgroundImgSrc}
		{backgroundColor}
		bind:scrollRef={mainLayoutScrollRef}
		isMobileApp={false}
		noScrollPage={(isDashWidgetList || isEnergyMonitoring || isAllDeviceWidgets) &&
			(!$is_mobile || isCameraMonitoring)}
		hideSlotStaticTop={!isDashWidgetList &&
			!isEnergyMonitoring &&
			!isCameraMonitoring &&
			!isAllDeviceWidgets &&
			currentTabItems.length === 0 &&
			!$listHeader}
		sidebarDesktopOnTop={$sidepanelDesktopOnTop}
	>
		<Header
			{useNewLayout}
			chromeStyle={headerChromeStyle}
			collapsed={isHeaderCollapsed || isCompactEditChrome}
			slot="header"
		>
			<div slot="leading" class="flex-1 min-width-0">
				<div class="d-flex align-items-center pt-xxs pb-xxs min-width-0 width-full">
					{#if $is_mobile}
						<DropdownProfile scrollMaxHeight={null} isPremium={!!$user_data.premium} />
					{:else if $is_tablet}
						<!-- Tablet: the rail is a horizontal bar, so keep the logo in the header. -->
						<button
							type="button"
							class="header-shelly-logo-btn flex-shrink-0"
							aria-label={$format('button_go_to_shelly', {
								default: 'Button go to Shelly',
							})}
							on:click={() => push(getLandingHref())}
						>
							<img
								src="/images/shelly-logo-circle.svg"
								alt=""
								class="header-shelly-logo"
							/>
						</button>
					{/if}
					<!-- Desktop: the logo lives at the top of the full-height menu rail (rendered by MainMenuProvider). -->
					<div class="flex-1 min-width-0 overflow-hidden header-title-stack">
						{#if activeHomeName && !isSettingsPage}
							<Text
								class={'mb-xs width-full min-width-0' + ($is_mobile ? ' ml-s' : '')}
								style="color: var(--color-white);"
								noWrap
								styleType="body-small"
							>
								{activeHomeName}
							</Text>
						{/if}
						<Text
							class={'text-capitalize width-full min-width-0' +
								($is_mobile ? ' ml-s' : '')}
							noWrap
							styleType="h2"
						>
							{$format(activeMenuItem?.label, {
								default: activeMenuItem?.labelDefault,
							})}
						</Text>
					</div>
				</div>
			</div>

			<!-- Left header section -->
			<div class={isSettingsPage ? 'd-flex gap-s-plus' : 'd-flex gap-s-plus mt-xs mt-lg-0'}>
				<AddAction energyOnly={energyWidgetPickerOnly} />

				<div class="separator-vertical header-divider" aria-hidden="true"></div>

				<NotificationCenter />

				{#if !accountSwitchingEnabled}
					<SimpleLogout />
				{:else if !$is_mobile}
					<DropdownProfile scrollMaxHeight={null} isPremium={!!$user_data.premium} />
				{/if}
			</div>
		</Header>

		<MainMenuProvider
			slot="mainMenu"
			bind:activeMenuItem
			bind:menuItems
			{visualActiveMenuId}
			on:menu-item-click={(e) => handlerClickMenuItem(e.detail.menuItem)}
			on:logo-click={() => push(getLandingHref())}
		/>

		<!-- <svelte:fragment slot="behindAndBelowLoader">
		<Text element="h2" class="mb-s">Pull down to refresh devices data.</Text>
	</svelte:fragment> -->

		<svelte:fragment slot="staticTop">
			{#if isDashWidgetList || isCameraMonitoring || isEnergyMonitoring || isAllDeviceWidgets}
				<!-- The rail's action slot (end of the rail) holds either the transparent
				     kebab (⋮) with all dashboard edits, or, while editing widgets, an "Exit edit
				     mode" button. Camera Monitoring is a default dashboard shown in this same
				     rail; its kebab (CameraDashActions) surfaces camera-specific settings instead. -->
				<Tabs
					{useNewLayout}
					tabsStyle={headerTabsStyle}
					class="mb-m"
					location={activeDashTabIndex}
					hideScrollArrows
					hideTabStrip={$compactEditChrome}
					exitDissolving={$editChromeExitDissolving}
				>
					<svelte:fragment slot="action">
						{#if isCameraMonitoring}
							<CameraDashActions
								manageDashboardMenuItems={cameraManageDashboardMenuItems}
								triggerVariant="muted"
							/>
						{:else if isAllEnergyWidgets}
							<AllEnergyWidgetsActions />
						{:else if isAllDeviceWidgets}
							<AllDeviceWidgetsActions />
						{:else if isEnergyMonitoring}
							<EnergyDashActions
								manageDashboardMenuItems={energyManageDashboardMenuItems}
							/>
						{:else if $edit || $editChromeExitDissolving}
							<ButtonV2
								variant="secondary"
								size="m"
								width="full"
								icon="fas fa-xmark"
								on:click={() => requestExitEditMode()}
							>
								{$format('button_exit_edit', { default: 'Exit edit mode' })}
							</ButtonV2>
						{:else}
							<DashActions
								menuListItemsData={dashMenuListItemsData}
								triggerVariant="muted"
							/>
						{/if}
					</svelte:fragment>
					{#each $dashboardTabs as item, index}
						{#if isDashTabVisible(item, isActivityLogVisible, isCameraMonitoringVisible, isEnergyMonitoringVisible)}
							{@const tabAvatar = resolveDashTabAvatar(item)}
							{@const isAllEnergyWidgetsTab = item.key === 'all_energy_widgets'}
							{@const isAllDeviceWidgetsTab = item.key === 'all_device_widgets'}
							{@const isTestingDashboardTab =
								isAllEnergyWidgetsTab || isAllDeviceWidgetsTab}
							{@const testingDashboardTabTooltip = isAllEnergyWidgetsTab
								? 'Virtual gallery of every energy widget type. Dev/testing only.'
								: 'Virtual gallery of all home devices as Quick Control vs Device Widget cards. Dev/testing only.'}
							{@const isTestingDashboardTabActive =
								isTestingDashboardTab && index === activeDashTabIndex}
							{#if isTestingDashboardTab}
								<div
									class="testing-dashboard-tab-wrap"
									class:is-active={isTestingDashboardTabActive}
									title={testingDashboardTabTooltip}
								>
									<span class="testing-dashboard-tab-tooltip" role="tooltip">
										{testingDashboardTabTooltip}
									</span>
									<Tab
										class="text-capitalize testing-dashboard-tab"
										{useNewLayout}
										tabsStyle={headerTabsStyle}
										notification={item.notification}
										active={index === activeDashTabIndex}
										hideRightSlot
										on:click={(event) => {
											event.preventDefault();
											handlerClickDashboardTab(item);
										}}
									>
										<RectangularAvatar
											slot="leftSlot"
											size="xs"
											icon={tabAvatar.icon}
											backgroundColor={tabAvatar.backgroundColor}
											backgroundImage={tabAvatar.backgroundImage}
										/>
										{item.text}
									</Tab>
								</div>
							{:else}
								<Tab
									class="text-capitalize"
									{useNewLayout}
									tabsStyle={headerTabsStyle}
									notification={item.notification}
									active={index === activeDashTabIndex}
									hideRightSlot
									on:click={(event) => {
										event.preventDefault();
										handlerClickDashboardTab(item);
									}}
								>
									<RectangularAvatar
										slot="leftSlot"
										size="xs"
										icon={tabAvatar.icon}
										backgroundColor={tabAvatar.backgroundColor}
										backgroundImage={tabAvatar.backgroundImage}
									/>
									{item.text}
								</Tab>
							{/if}
						{/if}
					{/each}
				</Tabs>
			{:else if isInRoomView}
				<RoomHeaderChrome {useNewLayout} tabsStyle={headerTabsStyle} />
			{:else if !isCameraMonitoring}
				{@const isSettingsTabs = $location.startsWith('/settings')}
				<Tabs
					{useNewLayout}
					tabsStyle={isSettingsTabs ? 'underline' : headerTabsStyle}
					class={isSettingsTabs ? 'mb-m settings-header-tabs' : 'mb-m'}
					location={headerQuickTabs ? headerQuickTabIndex : activeTabIndex}
					hideScrollArrows
					hideSlotAction={$location.indexOf('dash') === -1 && !$listHeader}
					hideTabStrip={$compactEditChrome}
					exitDissolving={$editChromeExitDissolving}
				>
					<svelte:fragment slot="action">
						{#if $location.indexOf('dash') !== -1}
							<IconButton
								aria-label={$format('button_edit', { default: 'Edit' })}
								icon="fas fa-pen"
								variant="secondary"
								on:click={() => {
									dashboardsEdit = true;
								}}
							/>
						{:else}
							<ListHeaderActions />
						{/if}
					</svelte:fragment>

					<!-- Quick filter tabs published by a list holder (no navigation) -->
					{#if headerQuickTabs}
						{#each headerQuickTabs.tabs as quickTab (quickTab.id)}
							<Tab
								class="text-capitalize"
								{useNewLayout}
								tabsStyle={headerTabsStyle}
								active={quickTab.id === headerQuickTabs.activeId}
								icon={quickTab.icon ?? ''}
								iconPosition="left"
								hideRightSlot
								on:click={(event) => {
									event.preventDefault();
									headerQuickTabs?.onSelect(quickTab.id);
								}}
							>
								{quickTab.label}
							</Tab>
						{/each}
					{/if}

					<!-- Settings tabs -->
					{#each currentTabItems as item, index}
						{#if isDashTabVisible(item, isActivityLogVisible, isCameraMonitoringVisible, isEnergyMonitoringVisible)}
							<Tab
								class="text-capitalize"
								{useNewLayout}
								tabsStyle={isSettingsTabs ? 'underline' : headerTabsStyle}
								notification={item.key === 'user_settings' && $isEmailUnverified
									? 'danger'
									: item.notification}
								active={index === activeTabIndex}
								icon={item.icon}
								on:click={(event) => {
									event.preventDefault();
									handlerClickTabItem(index);
								}}
								hideRightSlot={!item?.isPremium &&
									!item.circleAvatarIcon &&
									!item.beta &&
									!item.alpha}
							>
								{$format(item.text, { default: item.defaultText })}
								<TabRightSlotContent
									slot="rightSlot"
									{...item.beta
										? {
												labelProps: {
													size: 'xs',
													type: 'secondary',
													value: 'BETA',
												},
											}
										: {}}
									{...item.alpha
										? {
												labelProps: {
													size: 'xs',
													type: 'warning',
													value: 'alpha',
												},
											}
										: {}}
									{...item.isPremium
										? {
												circleAvatarProps: {
													size: 's',
													icon: item.circleAvatarIcon,
												},
											}
										: {}}
								/>
							</Tab>
						{/if}
					{/each}
				</Tabs>
			{/if}
		</svelte:fragment>

		<Router
			slot="scroll"
			routes={mainRoutes}
			on:routeLoading={routeLoading}
			on:routeLoaded={routeLoaded}
			bind:routeScrollRef
		/>

		<SidePanelProvider slot="drawerMenu" />
	</MainLayout>
</HomeBackground>

<!-- Modals -->
<IncludeAddModals />

<!-- =auth unlock modal // string/undefined -->
{#if typeof $authLockedDeviceModal !== 'undefined'}
	<UnlockAuthLockedDevice />
{/if}

<NewsletterModal bind:showModal={$showNewsletterModal} />

{#if $showNewLayoutOnboarding}
	<NewLayoutOnboardingModal
		bind:showModal={$showNewLayoutOnboarding}
		isEnabling
		on:close={markNewLayoutOnboardingSeen}
	/>
{/if}

<!-- Place ModalsContainer after Router but inside app-container -->
<ModalsContainer />

<!-- Calibration Has Started Modal, called by some devices when the device calibration is triggered externally -->
<CalibrationHasStartedModal
	bind:showModal={$showCalibrationHasStartedModal.visible}
	device={$showCalibrationHasStartedModal.device}
/>

<!-- Device reboot prompt, may be called by some devices when the device reboot is triggered externally -->
<RebootPrompt showModal={typeof $rebootRequired !== 'undefined'} />

<AlexaIntegrationPrompt />

<!-- <GeofenceRangeModal /> -->

<DashboardEditTabsModal bind:dashboardsEdit />

{#if typeof $showBleControlSetupModal !== 'undefined'}
	<BleControlModal />
{/if}

<AIChatOnboardingModal />

<AIChatPrivacyPolicyModal />

<AIChatModal />

{#if $showGlobalLoader}
	<LoaderDialog
		show={$showGlobalLoader}
		title={$globalLoaderMsg
			? $globalLoaderMsg
			: `${$format('loader_overlay_switching_profile_title', {
					default: 'Switching your profile.',
				})}..`}
	/>
{/if}

<NewDashManagementModal />

<AddEditDashboardNewLayout
	isEditDash
	bind:dashboardsEdit={showAddDashModal}
	bind:showDeleteModal={showDeleteDashModal}
/>

<!-- "Add dashboard" from the kebab reuses the add (non-edit) flow. -->
<AddEditDashboardNewLayout bind:dashboardsEdit={showNewDashModal} />

<EmEnergyDashboardSettingsModal bind:showModal={showEnergyDashSettingsModal} />

<!-- "Add widget" from the kebab opens the widget add wizard for the current dashboard. -->
{#if showAddWidgetModal}
	<DashWidgetActionModal
		bind:showModal={showAddWidgetModal}
		routeParams={$routeParams}
		isAddWizard
		energyOnly={energyWidgetPickerOnly}
	/>
{/if}

<style>
	/* Home background overlay + route-based overrides now live in HomeBackground.svelte. */

	/* Premium gem hangs outside the avatar; keep collapse clip only when collapsed. */
	:global(header.new-layout:not(.is-collapsed)) {
		overflow: visible;
	}

	.header-shelly-logo-btn {
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		margin-right: 40px;
	}

	.header-shelly-logo {
		display: block;
		width: 28px;
		height: 28px;
		border-radius: 50%;
	}

	.separator-vertical {
		align-self: center;
		height: 24px;
		border-right: 1px solid #fff;
		opacity: 0.5;
	}

	:global(header.settings-page-header) {
		margin: 0 0 0 24px !important;
	}

	:global(.main-layout.new-layout:has(.settings-page-header) .main) {
		padding-top: 0;
	}

	@media screen and (max-width: 1123px) {
		:global(header.settings-page-header) {
			margin-left: 16px !important;
		}
	}

	@media screen and (max-width: 639px) {
		:global(header.settings-page-header) {
			margin-left: 12px !important;
		}
	}

	/* Settings: white active segment centred on the 1px rail (DS sits it above). */
	:global(.settings-header-tabs.tabs-wrapper::after) {
		bottom: 1px;
	}
	:global(.settings-header-tabs.tabs-wrapper .tab.new-layout.active:not(:disabled)::before) {
		top: auto;
		bottom: 0;
		height: 3px;
		border-radius: 1px;
		background-color: var(--color-white);
		z-index: 4;
	}

	@media screen and (max-width: 1123px) {
		.header-shelly-logo-btn {
			margin-right: var(--spacing-l);
		}
	}

	/* When AI chat modal is open: lock layout scroll and block layout pointer-events (body class set by AISceneModalWrapper scroll lock). */
	:global(body.ai-scene-modal-open) {
		overflow: hidden;
		overscroll-behavior: none;
	}
	:global(body.ai-scene-modal-open .main-layout-scroll),
	:global(body.ai-scene-modal-open .main-layout .scroll-content-new-layout),
	:global(body.ai-scene-modal-open .scroll.scroll-drawer) {
		overflow: hidden !important;
		overscroll-behavior: none;
		touch-action: none;
		height: var(--ios-visual-viewport-height, 100vh);
		max-height: var(--ios-visual-viewport-height, 100vh);
	}
	:global(body.ai-scene-modal-open .main-layout) {
		pointer-events: none;
	}

	.testing-dashboard-tab-wrap {
		position: relative;
		display: inline-flex;
		align-items: center;
		border: 1px solid rgba(234, 179, 8, 0.75);
		border-radius: var(--radius-s, 8px);
		background: rgba(234, 179, 8, 0.1);
	}

	.testing-dashboard-tab-tooltip {
		position: absolute;
		bottom: calc(100% + 6px);
		left: 50%;
		transform: translateX(-50%);
		z-index: 20;
		width: max-content;
		max-width: 220px;
		padding: 4px 8px;
		border-radius: 6px;
		background: rgba(20, 20, 20, 0.95);
		color: #fff;
		font-size: 11px;
		line-height: 1.3;
		text-align: center;
		pointer-events: none;
		opacity: 0;
		visibility: hidden;
		transition: opacity 0.12s ease;
	}

	.testing-dashboard-tab-wrap:hover .testing-dashboard-tab-tooltip,
	.testing-dashboard-tab-wrap:focus-within .testing-dashboard-tab-tooltip {
		opacity: 1;
		visibility: visible;
	}

	@media (hover: none) {
		.testing-dashboard-tab-wrap.is-active .testing-dashboard-tab-tooltip {
			opacity: 1;
			visibility: visible;
		}
	}
</style>
